import {
  datadogLogs,
  HandlerType,
  LogsEvent,
  LogsInitConfiguration,
} from '@datadog/browser-logs'
import {
  IShellLogProvider,
  LogMessageContext,
} from '@sennder/senn-node-microfrontend-interfaces'
import {
  checkIfObject,
  deepClone,
  redactEmailAddress,
  trimPropertiesFromObject,
} from './utils'

type OptionalLogsInitConfiguration = Omit<
  LogsInitConfiguration,
  'clientToken' | 'service' | 'site' | 'env' | 'version'
>

type DatadogSite = LogsInitConfiguration['site']

const USER_ID_PROP = 'userId'
const EMAIL_PROP = 'email'

export default class DatadogLogger implements IShellLogProvider {
  private _userId?: string
  private _userEmail?: string
  private _canSendEvent: boolean
  private _maskSensitiveData: boolean

  constructor(
    clientToken: string,
    service: string,
    site: DatadogSite = 'datadoghq.eu',
    env = 'dev',
    version = '1.0.0',
    errorsToRedact: string[] = [],
    // optional param to extend or rewrite datadog logger init configs
    datadogLogOptions?: OptionalLogsInitConfiguration,
    maskSensitiveData = true
  ) {
    const message = (name: string): string => `${name} is not specified`
    if (!clientToken) {
      throw Error(message('Client token'))
    }
    if (!service) {
      throw Error(message('Service name'))
    }
    this._canSendEvent = true
    this._maskSensitiveData = maskSensitiveData
    this.initDatadogLogs(
      clientToken,
      service,
      site,
      env,
      version,
      errorsToRedact,
      datadogLogOptions
    )
  }

  public setUserData(userId: string, userEmail: string): void {
    this._userEmail = redactEmailAddress(userEmail, this._maskSensitiveData)
    this._userId = userId
    datadogLogs.setGlobalContextProperty(USER_ID_PROP, this._userId)
    datadogLogs.setGlobalContextProperty(EMAIL_PROP, this._userEmail)
  }

  public unsetUserData(): void {
    this._userEmail = undefined
    this._userId = undefined
    datadogLogs.removeGlobalContextProperty(USER_ID_PROP)
    datadogLogs.removeGlobalContextProperty(EMAIL_PROP)
  }

  public debug(
    message: string,
    messageContext: object,
    customProperties: string[] = []
  ): void {
    this.emitLogMessage('debug', message, messageContext, customProperties)
  }

  public info(
    message: string,
    messageContext: object,
    customProperties: string[] = []
  ): void {
    this.emitLogMessage('info', message, messageContext, customProperties)
  }

  public warn(
    message: string,
    messageContext: object,
    customProperties: string[] = []
  ): void {
    this.emitLogMessage('warn', message, messageContext, customProperties)
  }

  public error(
    message: string,
    messageContext: object,
    customProperties: string[] = [],
    error: Error | null = null
  ): void {
    this.emitLogMessage(
      'error',
      message,
      messageContext,
      customProperties,
      error
    )
  }

  public setHandlerType(value: HandlerType) {
    datadogLogs.logger.setHandler(value)
    this._canSendEvent = value !== HandlerType.silent
  }

  protected getHandlerType(): HandlerType | HandlerType[] {
    return datadogLogs.logger.getHandler()
  }

  private initDatadogLogs(
    clientToken: string,
    service: string,
    site: DatadogSite,
    env: string,
    version: string,
    errorsToRedact: string[],
    datadogLogOptions?: OptionalLogsInitConfiguration
  ): void {
    const initLogsConfiguration: LogsInitConfiguration = {
      clientToken,
      site,
      service,
      env,
      version,
    }
    datadogLogs.init({
      ...datadogLogOptions,
      ...initLogsConfiguration,
      beforeSend: (log: LogsEvent) => {
        if (!Array.isArray(errorsToRedact)) {
          return true
        }
        if (!errorsToRedact.every((error) => typeof error === 'string')) {
          return true
        }
        if (
          errorsToRedact.find((error: string) => log?.message.includes(error))
        ) {
          return false
        }
        return this._canSendEvent
      },
    })
  }

  private emitLogMessage(
    type: 'debug' | 'info' | 'warn' | 'error',
    message: string,
    messageContext: object,
    customProperties: string[] = [],
    error: Error | null = null
  ): void {
    if (!messageContext) {
      messageContext = {}
    } else if (!checkIfObject(messageContext)) {
      messageContext = {
        message: messageContext,
      }
    }
    // Clean the Body sent for failed HTTP calls
    // Could be sensitive data such as password when login fails.
    const cleanedContext = this.cleanMessageContext(
      messageContext,
      customProperties
    ) as Record<string, unknown>
    cleanedContext[USER_ID_PROP] = this._userId
    cleanedContext[EMAIL_PROP] = this._userEmail

    const cleanedContextError = cleanedContext.error
    const errorContext =
      error ||
      (cleanedContextError instanceof Error ? cleanedContextError : null)

    if (errorContext) {
      datadogLogs.logger.error(message, cleanedContext, errorContext)
    } else {
      datadogLogs.logger[type](message, cleanedContext)
    }
  }

  private cleanMessageContext(
    messageContext: LogMessageContext,
    customProperties: string[]
  ): object {
    const clone = deepClone(messageContext as Record<string, unknown>)
    const cloneWithError = clone as {
      error?: {
        config?: {
          data?: unknown
          headers?: {
            Authorization?: unknown
          }
        }
        request?: {
          __sentry_xhr__?: unknown
        }
      }
    }

    if (cloneWithError.error?.config?.data) {
      delete cloneWithError.error.config.data
    }

    if (cloneWithError.error?.config?.headers) {
      delete cloneWithError.error.config.headers.Authorization
    }

    if (cloneWithError.error?.request?.__sentry_xhr__) {
      delete cloneWithError.error.request.__sentry_xhr__
    }

    return trimPropertiesFromObject(clone, customProperties)
  }
}
